import React, { useState, useEffect } from 'react';
import {
  BarChart2,
  MessageSquare,
  Clock,
  CheckCircle2,
  Star,
  User,
  Tag,
  Filter,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { advancedReportsAPI } from '../../services/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

interface ConversationAuditsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  selectedAgent: number | 'all';
}

interface ConversationAudit {
  conversation_id: number;
  contact_name: string;
  agent_name: string | null;
  channel_type: string;
  created_at: string;
  resolved_at: string | null;
  status: string;
  priority: string;
  first_response_time: number | null;
  resolution_time: number | null;
  message_count: number;
  agent_message_count: number;
  customer_message_count: number;
  sentiment_score: number;
  csat_rating: number | null;
  labels: string[];
}

const getStatusVariant = (status: string): 'secondary' | 'outline' | 'destructive' => {
  switch (status.toLowerCase()) {
    case 'resolved':
      return 'secondary';
    case 'pending':
    case 'snoozed':
      return 'outline';
    default:
      return 'outline';
  }
};

const getPriorityVariant = (priority: string): 'destructive' | 'secondary' | 'outline' => {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'secondary';
    default:
      return 'outline';
  }
};

const getSentimentLabel = (score: number) => {
  if (score > 0.1) return 'Positive';
  if (score < -0.1) return 'Negative';
  return 'Neutral';
};

export const ConversationAudits: React.FC<ConversationAuditsProps> = ({
  dateRange,
  selectedAgent,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [audits, setAudits] = useState<ConversationAudit[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  useEffect(() => {
    fetchAudits();
  }, [dateRange, selectedAgent, page, statusFilter, priorityFilter]);

  const fetchAudits = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0],
        page,
        limit: 20,
      };

      if (selectedAgent !== 'all') params.agent_id = selectedAgent;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;

      const data = await advancedReportsAPI.getConversationAudits(params);
      setAudits(data);
      setTotalPages(Math.max(1, Math.ceil(data.length / 20)));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch conversation audits');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes: number | null) => {
    if (!minutes) return 'N/A';
    const roundedMinutes = Math.round(minutes * 10) / 10;
    if (roundedMinutes < 60) return `${roundedMinutes}m`;
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = Math.round(roundedMinutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Prepare data for export — untouched
  const exportData = audits || [];

  return (
    <div className="space-y-6">

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Filters</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="snoozed">Snoozed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priorityFilter || 'all'} onValueChange={(v) => setPriorityFilter(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Priorities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => {
                setStatusFilter('');
                setPriorityFilter('');
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {audits.length > 0 && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Conversations</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{audits.length}</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Resolved</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {audits.filter((a) => a.status === 'resolved').length}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg First Response</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatTime(
                  Math.round(
                    (audits.reduce((sum, a) => sum + (a.first_response_time || 0), 0) /
                      audits.length) *
                      10
                  ) / 10
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg CSAT Score</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(
                  audits.reduce((sum, a) => sum + (a.csat_rating || 0), 0) /
                    audits.filter((a) => a.csat_rating).length || 0
                ).toFixed(1)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Audits Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Conversation Details</CardTitle>
        </CardHeader>
        <CardContent>
          {audits.length === 0 ? (
            <Alert>
              <AlertDescription>
                No conversation audits found for the selected criteria.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Conversation</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Channel</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Messages</TableHead>
                      <TableHead>Response Time</TableHead>
                      <TableHead>Sentiment</TableHead>
                      <TableHead>CSAT</TableHead>
                      <TableHead>Labels</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audits.map((audit) => (
                      <TableRow key={audit.conversation_id}>
                        <TableCell>
                          <p className="text-sm font-medium">#{audit.conversation_id}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(audit.created_at)}
                          </p>
                        </TableCell>
                        <TableCell className="text-sm">{audit.contact_name}</TableCell>
                        <TableCell>
                          {audit.agent_name ? (
                            <span className="flex items-center gap-1 text-sm">
                              <User className="h-3 w-3 text-muted-foreground" />
                              {audit.agent_name}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">Unassigned</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {audit.channel_type.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusVariant(audit.status)} className="text-xs">
                            {audit.status.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getPriorityVariant(audit.priority)} className="text-xs">
                            {audit.priority.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{audit.message_count} total</p>
                          <p className="text-xs text-muted-foreground">
                            {audit.customer_message_count} customer, {audit.agent_message_count} agent
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{formatTime(audit.first_response_time)}</p>
                          {audit.resolution_time && (
                            <p className="text-xs text-muted-foreground">
                              Resolved in {formatTime(audit.resolution_time)}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">
                            {getSentimentLabel(audit.sentiment_score)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {audit.csat_rating ? (
                            <span className="flex items-center gap-1">
                              <Star className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm">{audit.csat_rating}/5</span>
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">No rating</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {audit.labels.slice(0, 2).map((label, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {label}
                              </Badge>
                            ))}
                            {audit.labels.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{audit.labels.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-center gap-3 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
